/*
 * st-preview: product preview that stacks pre-composited image layers.
 * Contract (design doc section 4): reads the existing Globo controls and the native Hardware option, looks the
 * exact values up in an inline manifest, loads every changed layer, then swaps all slots in one frame.
 * Preview rendering is read-only. The explicit cart-return URL path may activate the existing Globo colour and size
 * controls once; it never changes variants, Handle Position, add-ons, cart lines or hidden property mirrors.
 * Fails soft: on any error the component shows "not available" (colour layers hidden) and, when the very first
 * render cannot be shown, hands the gallery to the first real photo through the theme's own media-select event.
 * Audited 27 Oct 2026 (listener lifecycle, decode fallbacks, variant fallback, null layers, a11y) and corrected
 * 28 Oct 2026 after Astra's review: initial server-rendered layers are verified (a filename is not proof of a
 * load), timed-out loads are evicted so a retry starts fresh, idle work is cancelled on teardown, the decoded-image
 * cache has a byte budget (smaller on phones), and the example colourway shown for unselected fields is marked.
 * Recheck (same day): `current` holds VERIFIED layers only, so a pending server-rendered layer is awaited by every
 * load that needs it (not by a one-shot promise), is covered by the timeout eviction, and can never be shortcut to
 * "ready" by a second unchanged event; a superseded load's timers are cleared; the state stays "static" (the
 * server-rendered picture as delivered) until the first verified render instead of being declared ready at boot.
 * Authoritative copy: prototype/preview.js; theme-patch/assets/product-preview.js is a byte-identical copy.
 */
(function () {
  'use strict';
  if (window.__stPreviewLoaded) return; // the asset tag may be emitted by more than one section
  window.__stPreviewLoaded = true;

  var SETTLE_MS = 80, UPDATING_AFTER_MS = 150, TIMEOUT_MS = 8000;
  var CACHE_ENTRIES = 24, CACHE_BYTES_DESKTOP = 96 * 1048576, CACHE_BYTES_PHONE = 40 * 1048576;
  var own = Object.prototype.hasOwnProperty;

  // Small LRU of decoded-image entries with an entry cap and a byte budget (entry.size = decoded RGBA bytes once the
  // image has loaded; 0 while pending). The budget is an upper bound on what this component asks the browser to
  // keep decoded; browsers may discard decoded surfaces of off-screen images on their own.
  function Cache(maxEntries, maxBytes) { this.n = maxEntries; this.maxBytes = maxBytes; this.m = new Map(); }
  Cache.prototype.get = function (k) { if (!this.m.has(k)) return undefined; var v = this.m.get(k); this.m.delete(k); this.m.set(k, v); return v; };
  Cache.prototype.set = function (k, v) { this.m.delete(k); this.m.set(k, v); this.trim(); };
  Cache.prototype.delete = function (k) { this.m.delete(k); };
  Cache.prototype.peek = function (k) { return this.m.get(k); }; // no recency update
  Cache.prototype.bytes = function () { var b = 0; this.m.forEach(function (v) { b += v.size || 0; }); return b; };
  Cache.prototype.trim = function () {
    while (this.m.size > this.n || (this.m.size > 1 && this.bytes() > this.maxBytes)) this.m.delete(this.m.keys().next().value);
  };

  function url(manifest, file, width) {
    // Files URLs from Shopify carry "?v=…" and take "&width="; a URL without a query takes "?width="
    var pattern = manifest.urlPattern || '{file}&width={width}';
    if (file.indexOf('?') < 0) pattern = pattern.replace('&width=', '?width=');
    return pattern.replace('{cdnBase}', manifest.cdnBase || '').replace('{file}', file).replace('{width}', String(width));
  }
  function srcset(manifest, file) {
    return manifest.widths.map(function (w) { return url(manifest, file, w) + ' ' + w + 'w'; }).join(', ');
  }
  function fallbackWidth(manifest) {
    // the src used by browsers without srcset support and as the preload candidate: the step nearest 1200
    var best = manifest.widths[0];
    manifest.widths.forEach(function (w) { if (Math.abs(w - 1200) < Math.abs(best - 1200)) best = w; });
    return best;
  }
  function readProductJson(section) {
    var el = section && section.querySelector('[data-product-json]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function cssEscape(s) { return String(s).replace(/["\\]/g, '\\$&'); }
  function phoneLike() {
    try { return !!(window.matchMedia && (matchMedia('(max-width: 749px)').matches || matchMedia('(pointer: coarse)').matches)); } catch (e) { return false; }
  }
  function decodedBytes(img) { return img && img.naturalWidth ? img.naturalWidth * img.naturalHeight * 4 : 0; }

  function StPreview(root) {
    this.root = root;
    this.seq = 0;
    this.latest = 0;
    this.cache = new Cache(CACHE_ENTRIES, phoneLike() ? CACHE_BYTES_PHONE : CACHE_BYTES_DESKTOP);
    this.current = {};          // role -> file currently displayed AND verified loaded (never a pending or failed layer)
    this.state = 'static';      // the server-rendered picture as delivered; "ready" only after a verified render
    this.inflight = null;       // clears the timers of the load in progress (superseded loads must not fire them)
    this.warned = false;
    this.firstPaint = true;     // until the first verified render; a failure before then falls back to a real photo
    this.fellBack = false;
    this.destroyed = false;
    this.offs = [];             // listener removers (destroy())
    this.idles = [];            // idle-callback / timeout handles (destroy())
    this.debug = root.hasAttribute('data-debug');
    try { this.init(); } catch (e) { this.fail(e); }
  }

  StPreview.prototype.fail = function (e) {
    this.galleryReturn = null;
    if (!this.warned) { this.warned = true; console.warn('[st-preview] disabled:', e && e.message ? e.message : e); }
    this.setState('static');
    // a component that cannot run must not leave a blank hero: if a server-rendered layer visibly failed, hand over
    try { if (this.firstPaint && this.initialBroken()) this.fallbackToPhoto(); } catch (err) {}
  };
  StPreview.prototype.initialBroken = function () {
    var imgs = this.root.querySelectorAll('img[data-role][data-file]');
    for (var i = 0; i < imgs.length; i++) if (imgs[i].complete && !imgs[i].naturalWidth) return true;
    return false;
  };

  StPreview.prototype.init = function () {
    var manifestEl = this.root.querySelector('script[data-st-manifest]');
    if (!manifestEl) throw new Error('manifest missing');
    this.manifest = JSON.parse(manifestEl.textContent);
    if (this.manifest.schema !== 1) throw new Error('unsupported manifest schema ' + this.manifest.schema);
    this.views = this.manifest.views || {};
    this.viewBy = this.manifest.viewBy || null;
    var keys = Object.keys(this.views), want = this.root.getAttribute('data-view') || 'front';
    if (own.call(this.views, want)) this.defaultView = want;
    else if (this.viewBy && keys.length) this.defaultView = keys[0];
    else throw new Error('view missing');
    for (var k = 1; k < keys.length; k++) if (String(this.views[keys[k]].order) !== String(this.views[keys[0]].order)) throw new Error('views differ in layer order');
    this.view = this.views[this.defaultView];

    this.section = this.root.closest('[data-section-type="product"], [data-section-type="featured-product"]');
    if (!this.section) throw new Error('product section not found');
    this.product = readProductJson(this.section);
    if (!this.product || String(this.product.id) !== String(this.manifest.product.id)) throw new Error('manifest product does not match page product');

    // the variant option that drives the hardware roles (if any)
    this.optIndex = {}; this.hwIndex = 0;
    for (var r in this.view.roles) {
      if (!own.call(this.view.roles, r) || this.view.roles[r].source.type !== 'variant') continue;
      this.optIndex[r] = this.optionIndex(this.view.roles[r].source.option);
      if (!this.hwIndex) this.hwIndex = this.optIndex[r];
    }
    this.viewIndex = this.viewBy ? this.optionIndex(this.viewBy.option) : 0;
    this.view = this.views[this.viewKey()];

    this.stage = this.root.querySelector('.st-preview__stage');
    this.layersEl = this.root.querySelector('.st-preview__layers');
    this.status = this.root.querySelector('.st-preview__status');
    this.combo = this.root.querySelector('.st-preview__combo');
    this.thumb = document.querySelector('st-preview-thumb[data-for="' + cssEscape(this.root.id) + '"]');
    if (!this.stage || !this.layersEl) throw new Error('stage missing');
    this.root.setAttribute('data-view', this.viewKey());
    this.setAspect();

    // slots: reuse server-rendered <img> elements, create any that are missing (hidden until they have a file)
    var self = this;
    this.slots = {};
    this.view.order.forEach(function (role) {
      var img = self.layersEl.querySelector('img[data-role="' + cssEscape(role) + '"]');
      if (!img) {
        img = document.createElement('img');
        img.className = 'st-preview__layer';
        img.setAttribute('data-role', role);
        img.alt = '';
        img.width = self.manifest.canvas.width; img.height = self.manifest.canvas.height;
        img.decoding = 'async';
        img.setAttribute('data-hidden', '');
        self.layersEl.appendChild(img);
      }
      img.sizes = self.manifest.sizes;
      self.slots[role] = img;
    });
    if (this.thumb) {
      this.thumbSlots = {};
      this.view.order.forEach(function (role) {
        var img = self.thumb.querySelector('img[data-role="' + cssEscape(role) + '"]');
        if (!img) { img = document.createElement('img'); img.className = 'st-preview-thumb__layer'; img.setAttribute('data-role', role); img.alt = ''; img.width = self.manifest.canvas.width; img.height = self.manifest.canvas.height; img.setAttribute('data-hidden', ''); self.thumb.appendChild(img); }
        img.sizes = self.thumb.getAttribute('data-sizes') || '90px';
        self.thumbSlots[role] = img;
      });
    }
    this.adoptInitial();

    this.bind();
    this.restoreCartColours();
    this.schedule('init');      // the first onChange verifies (loads) the server-rendered layers before any "ready"
    this.prefetchAtIdle();
  };

  // The server-rendered layers are adopted as cache entries whose readiness is the real load state of the <img>: a
  // data-file attribute is not proof that the file was delivered. Only a layer that has already loaded (complete,
  // with a natural size) is recorded in `current`. A pending one is loaded through the normal path by the first
  // onChange (the cache hands back this entry, so no second request is made, and the load's timeout bookkeeping
  // covers it); a layer that already failed is hidden and reloaded there, so its failure is handled like any other.
  StPreview.prototype.adoptInitial = function () {
    var self = this;
    this.view.order.forEach(function (role) {
      var img = self.slots[role], file = img.getAttribute('data-file');
      if (!file) return;
      if (img.complete && !img.naturalWidth) { img.setAttribute('data-hidden', ''); return; } // already failed
      var entry = { file: file, img: img, done: false, size: 0, adopted: true };
      if (img.complete) {
        entry.done = true; entry.size = decodedBytes(img); entry.ready = Promise.resolve(true);
        self.current[role] = file;
      } else {
        entry.ready = new Promise(function (resolve, reject) {
          img.addEventListener('load', function () { resolve(true); }, { once: true });
          img.addEventListener('error', function () { reject(new Error('initial layer failed: ' + role)); }, { once: true });
        }).then(function () { entry.done = true; entry.size = decodedBytes(img); self.cache.trim(); return true; });
        entry.ready.catch(function () { entry.done = true; if (self.cache.get(file) === entry) self.cache.delete(file); }); // never pin a failure
      }
      self.cache.set(file, entry);
    });
  };

  // Explicit cart-return navigation: restore through Globo's controls, never through a preview-only override.
  StPreview.prototype.restoreCartColours = function () {
    if (this.destroyed || window.__stCartColourRestoreHandled || !window.location ||
        this.section.getAttribute('data-section-type') !== 'product') return;
    var adapter = this.manifest.product.handle === 'handsfree-leash-adapter' && String(this.manifest.product.id) === '7063451402410';
    var collar = this.manifest.product.handle === 'adjustable-biothane-quick-release-collar-updated' && String(this.manifest.product.id) === '7892919877802';
    var classic = (this.manifest.product.handle === 'classic-biothane-dog-leash-all-colours' && String(this.manifest.product.id) === '15426609217706') || (this.manifest.product.handle === 'classic-biothane-leash-terracotta' && String(this.manifest.product.id) === '6904941707434');
    var accessoryIds = { 'safety-strap-draft-build': '15426793898154', 'multi-dog-attachment-draft-build': '15426793996458', 'safety-strap': '7882843947178', 'multi-dog-attachment': '7881533489322', 'classic-biothane-dog-collar-all-colours': '15426609905834' };
    var accessory = accessoryIds[this.manifest.product.handle] === String(this.manifest.product.id);
    if (!adapter && !collar && !accessory && !classic && this.manifest.product.handle !== 'biothane-traffic-handle-leash') return;
    var accentField = collar ? 'Size' : (adapter ? 'Accent Colour' : 'Handle Colour');
    var accentParam = collar ? 'pv_size' : (adapter ? 'pv_accent_colour' : 'pv_handle_colour');
    var main = this.view.roles.main, accent = this.view.roles.accent;
    if (!main || main.source.type !== 'globo' || main.source.field !== 'Colour') return;
    if ((collar || accessory) ? Boolean(accent) : (!accent || accent.source.type !== 'globo' || accent.source.field !== accentField)) return;
    var params = new URLSearchParams(window.location.search);
    if (params.getAll('pv_colour').length !== 1 || (!accessory && params.getAll(accentParam).length !== 1)) return;
    var wantedMain = params.get('pv_colour'), wantedAccent = params.get(accentParam);
    // HFA still offers Hibiscus in both actual fields. Restore it only when that enabled option exists;
    // it remains unmapped by the image resolver and cannot become an invented layer filename.
    if ((!own.call(main.values, wantedMain) && !((adapter || collar) && wantedMain === 'Hibiscus')) ||
        (!accessory && (collar ? !/^(S|M|L|XL)$/.test(wantedAccent) : (!own.call(accent.values, wantedAccent) && wantedAccent !== 'Hibiscus')))) return;
    window.__stCartColourRestoreHandled = true;
    var self = this, active = true, busy = false, pending = null, observer, deadline;
    var applied = {}, removers = [];
    var values = { Colour: wantedMain };
    if (!accessory) values[accentField] = wantedAccent;
    function control(name) {
      var inputs = self.section.querySelectorAll('.gpo-app [data-field-name="' + cssEscape(name) + '"]');
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        if (input.disabled) continue;
        if (input.tagName === 'INPUT' && input.type === 'radio' && input.value === values[name]) return input;
        if (input.tagName === 'SELECT') {
          for (var j = 0; j < input.options.length; j++) {
            if (input.options[j].value === values[name] && !input.options[j].disabled) return input;
          }
        }
      }
      return null;
    }
    function stop() {
      active = false;
      if (observer) observer.disconnect();
      clearTimeout(pending); clearTimeout(deadline);
      removers.forEach(function (remove) { remove(); }); removers = [];
      self.cartRestoreStop = null;
    }
    function listen(target, name, fn, options) {
      target.addEventListener(name, fn, options);
      removers.push(function () { target.removeEventListener(name, fn, options); });
    }
    function apply(name, input) {
      applied[name] = true;
      if (input.tagName === 'SELECT') {
        if (input.value === values[name]) return;
        input.value = values[name];
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!input.checked) input.click();
    }
    function attempt() {
      pending = null;
      if (!active || self.destroyed || busy) return;
      var colour = control('Colour'), accentColour = accessory ? null : control(accentField);
      if (!colour || (!accessory && !accentColour)) return;
      busy = true;
      try {
        if (!applied.Colour) apply('Colour', colour);
        // Globo may replace the second field in response to the first change: use its current live control.
        accentColour = accessory ? null : control(accentField);
        if (accentColour && !applied[accentField]) apply(accentField, accentColour);
        if (applied.Colour && (accessory || applied[accentField])) { stop(); self.schedule('cart-colours'); }
      } catch (error) { stop(); }
      busy = false;
    }
    function queue() {
      if (active && !busy && !pending) pending = setTimeout(attempt, 0);
    }
    function userInteraction(event) {
      if (!busy && event.isTrusted && event.target && event.target.closest && event.target.closest('.gpo-app')) stop();
    }
    ['pointerdown', 'keydown', 'click', 'input', 'change'].forEach(function (name) {
      listen(self.section, name, userInteraction, { capture: true, passive: true });
    });
    listen(document, 'gpoRenderCompleted', queue);
    observer = new MutationObserver(queue);
    observer.observe(this.section.querySelector('.product__content') || this.section, { childList: true, subtree: true });
    deadline = setTimeout(stop, 15000);
    this.cartRestoreStop = stop;
    queue();
  };

  // ---------- reading the existing controls (never writing) ----------
  StPreview.prototype.field = function (name) {
    // Globo renders the field as a <select>, radios (swatches/buttons) or checkboxes, plus a hidden mirror input
    // with the same data-field-name in some versions; take the first real control, fall back to the mirror.
    var els = this.section.querySelectorAll('.gpo-app [data-field-name="' + cssEscape(name) + '"]'), mirror = '';
    for (var i = 0; i < els.length; i++) {
      var el = els[i], type = (el.type || '').toLowerCase();
      if (el.tagName === 'SELECT') return el.value || '';
      if (type === 'radio' || type === 'checkbox') { if (el.checked) return el.value; continue; }
      if (el.tagName === 'INPUT' && !mirror) mirror = el.value || '';
    }
    return mirror;
  };
  StPreview.prototype.optionIndex = function (name) {
    var i = this.product.options.findIndex(function (o) { return (o.name || o) === name; }) + 1;
    if (!i) throw new Error('option "' + name + '" not found in product JSON');
    return i;
  };
  StPreview.prototype.variantOptionValue = function (role) { return this.optionValue(role && this.optIndex && this.optIndex[role] ? this.optIndex[role] : this.hwIndex); };
  StPreview.prototype.viewValue = function () { return this.viewBy ? this.optionValue(this.viewIndex) : ''; };
  StPreview.prototype.viewKey = function () {
    if (!this.viewBy) return this.defaultView;
    var v = this.viewValue(), key = v && own.call(this.viewBy.values, v) ? this.viewBy.values[v] : null;
    return key && own.call(this.views, key) ? key : this.defaultView;
  };
  StPreview.prototype.setView = function (key) {
    if (!key || !own.call(this.views, key) || this.view === this.views[key]) return;
    this.view = this.views[key];
    this.root.setAttribute('data-view', key);
    this.setAspect();
  };
  StPreview.prototype.setAspect = function () {
    var c = this.view.canvas || this.manifest.canvas, v = c.width + ' / ' + c.height;
    this.root.style.setProperty('--st-aspect', v);
    if (this.thumb) this.thumb.style.setProperty('--st-aspect', v);
  };
  StPreview.prototype.optionValue = function (index) {
    if (!index) return '';
    var idx = 'option' + index;
    var ctrl = this.section.querySelector('input[data-single-option-selector][data-index="' + idx + '"]:checked, select[data-single-option-selector][data-index="' + idx + '"]');
    if (ctrl && ctrl.value) return ctrl.value;
    // fallback: the variant id the theme keeps in the (form-associated) name="id" control
    var form = this.section.querySelector('form[data-product-form]');
    var idCtrl = form && form.elements && form.elements.namedItem('id');
    if (!idCtrl) idCtrl = this.section.querySelector('input[name="id"]');
    var id = idCtrl ? String(idCtrl.value) : '';
    var variant = id && this.product.variants.find(function (v) { return String(v.id) === id; });
    return variant ? (variant[idx] || '') : '';
  };
  StPreview.prototype.readSelection = function () {
    var roles = this.view.roles, sel = {};
    for (var role in roles) {
      if (!own.call(roles, role)) continue;
      var src = roles[role].source;
      if (src.type === 'globo') sel[role] = this.field(src.field);
      else if (src.type === 'variant') sel[role] = this.variantOptionValue(role);
    }
    return sel;
  };

  // ---------- resolve selection -> files ----------
  StPreview.prototype.resolve = function (sel) {
    var m = this.manifest, key = this.viewKey(), v = this.views[key], files = { base: v.base }, example = {}, missing = [];
    for (var role in v.roles) {
      if (!own.call(v.roles, role)) continue;
      var def = v.roles[role], value = sel[role];
      if (value) {
        if (own.call(def.values, value)) files[role] = def.values[value]; // null = this value has no layer (baked into the base)
        else missing.push(role + '=' + value);                             // a store value with no layer (e.g. Hibiscus): unavailable, selection untouched
      } else if (def.source.type === 'globo') {
        // nothing chosen yet: show the example colourway (marked as such) or the neutral layer; never a form default
        var d = m.defaults && m.defaults[role];
        if ((m.unselectedMode || 'example') === 'example' && d && own.call(def.values, d)) { files[role] = def.values[d]; example[role] = d; }
        else if (def.unselected) files[role] = def.unselected;
        else missing.push(role + '=(none)');
      } else missing.push(role + '=(none)');
    }
    return { files: files, example: example, missing: missing, selection: sel, view: key, viewValue: this.viewBy ? (v.value || this.viewValue()) : '' };
  };

  // ---------- scheduling, loading, atomic swap ----------
  StPreview.prototype.schedule = function (why) {
    var self = this;
    if (this.raf || this.destroyed) return;
    this.raf = requestAnimationFrame(function () {
      self.raf = null;
      if (self.destroyed) return;
      try { self.onChange(why); } catch (e) { self.fail(e); }
    });
  };
  StPreview.prototype.onChange = function (why) {
    var req = this.resolve(this.readSelection());
    req.seq = ++this.seq; req.why = why; req.t0 = performance.now();
    this.latest = req.seq;
    clearTimeout(this.settle);
    if (req.missing.length) { this.showUnavailable(req); return; }
    var changed = false;
    for (var role in req.files) if (own.call(req.files, role) && (req.files[role] || null) !== (this.current[role] || null)) { changed = true; break; }
    // every layer this request shows is displayed and verified (current holds verified layers only): nothing to load
    if (!changed) { this.firstPaint = false; this.setView(req.view); this.describe(req); if (this.state !== 'ready') this.setState('ready'); this.returnToPreview(req); return; }
    var self = this;
    this.settle = setTimeout(function () { try { self.load(req); } catch (e) { self.fail(e); } }, why === 'init' ? 0 : SETTLE_MS);
  };
  StPreview.prototype.image = function (file) {
    var hit = this.cache.get(file);
    if (hit) return hit;
    var self = this, img = new Image();
    img.sizes = this.manifest.sizes;
    img.srcset = srcset(this.manifest, file);
    img.src = url(this.manifest, file, fallbackWidth(this.manifest));
    var loaded = new Promise(function (resolve, reject) {
      if (img.complete) return img.naturalWidth ? resolve() : reject(new Error('load failed: ' + file));
      img.addEventListener('load', function () { resolve(); }, { once: true });
      img.addEventListener('error', function () { reject(new Error('load failed: ' + file)); }, { once: true });
    });
    var entry = { file: file, img: img, done: false, size: 0 };
    entry.ready = loaded.then(function () {
      // decode() is best effort: a rejection after a successful load (cached image, detached element) is not a failure
      return typeof img.decode === 'function' ? img.decode().catch(function () {}) : undefined;
    }).then(function () { entry.done = true; entry.size = decodedBytes(img); self.cache.trim(); return true; });
    entry.ready.catch(function () { entry.done = true; if (self.cache.get(file) === entry) self.cache.delete(file); }); // never pin a failure
    this.cache.set(file, entry);
    return entry;
  };
  StPreview.prototype.load = function (req) {
    var self = this;
    if (req.seq !== this.latest) return;
    if (this.inflight) this.inflight();                 // the superseded load's timers must not fire against this one's entries
    var updatingTimer = setTimeout(function () { if (req.seq === self.latest) self.setState('updating'); }, UPDATING_AFTER_MS);
    var timeoutTimer;
    var timeout = new Promise(function (_, reject) { timeoutTimer = setTimeout(function () { reject(new Error('timeout')); }, TIMEOUT_MS); });
    var clear = function () { clearTimeout(updatingTimer); clearTimeout(timeoutTimer); if (self.inflight === clear) self.inflight = null; };
    this.inflight = clear;
    // current holds verified layers only, so a server-rendered layer still loading is "needed" too: the cache returns
    // its adopted entry (no second request), and it is awaited, timed out and evicted like any other entry
    var needed = Object.keys(req.files).filter(function (role) { return req.files[role] && req.files[role] !== self.current[role]; });
    var entries = needed.map(function (role) { return self.image(req.files[role]); });
    var loads = entries.map(function (e) { return e.ready; });
    Promise.race([Promise.all(loads), timeout]).then(function () {
      clear();
      if (req.seq !== self.latest) return;              // superseded: keep in cache, do not display
      requestAnimationFrame(function () {
        if (req.seq !== self.latest) return;
        try { self.swap(req); } catch (e) { self.fail(e); }
      });
    }, function (err) {
      clear();
      if (err && err.message === 'timeout') {
        // a stalled request must not stay cached: evict the entries that are still pending (identity-checked, so a
        // newer entry for the same file is never removed); the next selection of that colour starts a fresh load
        entries.forEach(function (e) { if (!e.done && self.cache.get(e.file) === e) self.cache.delete(e.file); });
      }
      if (req.seq !== self.latest) return;
      req.error = err && err.message;
      self.showUnavailable(req);
      if (self.firstPaint) self.fallbackToPhoto();
    });
  };
  StPreview.prototype.swap = function (req) {
    var self = this;
    this.setView(req.view);
    this.view.order.forEach(function (role) {
      var file = req.files[role], slot = self.slots[role];
      if (!slot) return;
      if (!file) { slot.setAttribute('data-hidden', ''); self.current[role] = null; if (self.thumbSlots && self.thumbSlots[role]) self.thumbSlots[role].setAttribute('data-hidden', ''); return; }
      // Keep a successfully loaded same-file slot. Reset pending or broken slots: a detached retry loading the
      // same URL does not repair an original element whose failed request is complete with no natural size.
      if (slot.getAttribute('data-file') !== file || !slot.complete || !slot.naturalWidth) {
        slot.srcset = srcset(self.manifest, file);
        slot.src = url(self.manifest, file, fallbackWidth(self.manifest));
        slot.setAttribute('data-file', file);
      }
      if (file !== self.current[role]) {
        // an adopted entry's image IS this slot: once the slot moves on it no longer holds that file decoded
        var old = self.current[role], oldEntry = old && self.cache.peek(old);
        if (oldEntry && oldEntry.img === slot) self.cache.delete(old);
        self.current[role] = file;
      }
      slot.removeAttribute('data-hidden');
      if (self.thumbSlots && self.thumbSlots[role]) {
        var t = self.thumbSlots[role];
        if (t.getAttribute('data-file') !== file) { t.srcset = srcset(self.manifest, file); t.src = url(self.manifest, file, self.manifest.widths[0]); t.setAttribute('data-file', file); }
        t.removeAttribute('data-hidden');
      }
    });
    this.firstPaint = false;
    this.describe(req);
    this.setState('ready');
    this.returnToPreview(req);
    if (this.debug) console.info('[st-preview] ready after ' + Math.round(performance.now() - req.t0) + ' ms (' + req.why + ')');
    this.emit('ready', req);
  };
  StPreview.prototype.showUnavailable = function (req) {
    this.galleryReturn = null;
    var self = this;
    this.view.order.forEach(function (role) {
      var def = self.view.roles[role];
      var hide = !req.files[role] || (def && def.source.type === 'globo') || self.current[role] !== req.files[role];
      if (self.slots[role]) { if (hide) self.slots[role].setAttribute('data-hidden', ''); else self.slots[role].removeAttribute('data-hidden'); }
      if (self.thumbSlots && self.thumbSlots[role]) { if (hide) self.thumbSlots[role].setAttribute('data-hidden', ''); else self.thumbSlots[role].removeAttribute('data-hidden'); }
    });
    this.view.order.forEach(function (role) { if (self.view.roles[role] && self.view.roles[role].source.type === 'globo') delete self.current[role]; });
    if (this.status) this.status.textContent = this.text('unavailable', 'Preview not available for this selection. The product photos show the colours.');
    if (this.combo) { this.combo.textContent = ''; this.combo.hidden = true; }
    this.root.removeAttribute('data-example');
    this.stage.setAttribute('aria-label', this.text('label-unavailable', 'Product preview not available for this selection'));
    this.setState('unavailable');
    this.emit('unavailable', req);
  };
  // The first render could not be shown: make the first real photo the active slide, through the theme's own
  // media-select event (the same event its thumbnails dispatch), so the gallery's fader, thumbs and zoom stay
  // consistent. The preview slide and thumbnail remain, marked unavailable, so the shopper can return to it.
  StPreview.prototype.fallbackToPhoto = function () {
    if (this.fellBack) return;
    this.fellBack = true;
    var slide = this.root.closest('[data-slide]');
    if (!slide) return;
    var gallery = slide.parentNode, isPreview = function (el) { return !!el.querySelector('st-preview'); };
    var isPhoto = function (el) { return !!el.getAttribute('data-media-id') && !isPreview(el); };
    if (gallery && gallery.querySelectorAll) {
      var active = null, activeId = gallery.getAttribute('data-active-media'), slides = gallery.querySelectorAll('[data-slide]');
      for (var i = 0; i < slides.length; i++) {
        if (activeId ? slides[i].getAttribute('data-media-id') === activeId : slides[i].matches('.media--active')) { active = slides[i]; break; }
      }
      if (active && active !== slide) return;
    }
    var next = slide.nextElementSibling;
    while (next && !isPhoto(next)) next = next.nextElementSibling;
    if (!next && gallery && gallery.querySelectorAll) { var all = gallery.querySelectorAll('[data-slide]'); for (var j = 0; j < all.length; j++) if (isPhoto(all[j])) { next = all[j]; break; } }
    var id = next && next.getAttribute('data-media-id');
    if (!id) return;
    if (this.status) this.status.textContent = this.text('fallback', 'Preview not available. Showing the product photos.');
    this.root.dispatchEvent(new CustomEvent('theme:media:select', { bubbles: true, detail: { id: id } }));
    this.emit('fallback', { id: id });
  };
  StPreview.prototype.describe = function (req) {
    var label = [], isExample = false;
    for (var role in this.view.roles) {
      if (!own.call(this.view.roles, role)) continue;
      var def = this.view.roles[role];
      var name = def.source.field || def.source.option;
      var value = req.selection[role] || req.example[role] || '';
      if (req.example[role]) isExample = true;
      if (!value) continue;
      label.push(value + ' ' + String(name).toLowerCase());
    }
    if (req.viewValue) label.push(req.viewValue + ' ' + String(this.viewBy.option).toLowerCase());
    this.stage.setAttribute('aria-label', (isExample ? this.text('label-example', 'Example product preview') : this.text('label', 'Product preview')) + ': ' + label.join(', '));
    // the example colourway (one or both colour fields still unselected) is said so: it is not the shopper's choice
    // and does not make the required fields complete
    if (isExample) this.root.setAttribute('data-example', ''); else this.root.removeAttribute('data-example');
    if (this.status) this.status.textContent = '';
    if (this.combo) {
      var main = req.selection.main || req.example.main || '', accent = req.selection.accent || req.example.accent || '';
      var text = main && accent ? main + ' x ' + accent : main || '';
      this.combo.textContent = text ? (isExample ? this.text('example-prefix', 'Example: ') + text : text) : '';
      this.combo.hidden = !text;
    }
    this.emit('describe', { example: isExample, selection: req.selection }); // product-layout.js re-reads the line on this
  };
  // UI strings can be supplied by Liquid as data-text-<key> attributes on <st-preview> (for localisation)
  StPreview.prototype.text = function (key, fallback) { return this.root.getAttribute('data-text-' + key) || fallback; };
  StPreview.prototype.setState = function (s) {
    this.state = s;
    this.root.setAttribute('data-state', s);
    this.root.setAttribute('aria-busy', s === 'updating' ? 'true' : 'false');
    if (s === 'updating' && this.status) this.status.textContent = this.text('updating', 'Updating preview\u2026');
    this.emit('state', { state: s });
  };
  StPreview.prototype.emit = function (type, detail) {
    this.root.dispatchEvent(new CustomEvent('st-preview:' + type, { bubbles: true, detail: detail }));
  };

  // ---------- prefetch ----------
  function saveData() { var c = navigator.connection; return !!(c && c.saveData); }
  function fastConnection() { var c = navigator.connection; return !c || (!c.saveData && (!c.effectiveType || c.effectiveType === '4g')); }
  // the layer a control (radio/select option) stands for, or null
  StPreview.prototype.fileForControl = function (input) {
    if (!input || !this.section.contains(input)) return null;
    var roles = this.view.roles, value = input.value;
    if (!value) return null;
    if (this.viewBy && input.hasAttribute('data-single-option-selector') && input.getAttribute('data-index') === 'option' + this.viewIndex) {
      var key = own.call(this.viewBy.values, value) ? this.viewBy.values[value] : null, v = key && own.call(this.views, key) ? this.views[key] : null;
      return v && v.base !== this.current.base ? v.base : null;
    }
    for (var role in roles) {
      if (!own.call(roles, role)) continue;
      var src = roles[role].source, hit = false;
      if (src.type === 'globo') hit = input.getAttribute('data-field-name') === src.field;
      else if (src.type === 'variant') hit = input.hasAttribute('data-single-option-selector') && input.getAttribute('data-index') === 'option' + (this.optIndex && this.optIndex[role] ? this.optIndex[role] : this.hwIndex);
      if (!hit) continue;
      var file = own.call(roles[role].values, value) ? roles[role].values[value] : null;
      return file && file !== this.current[role] ? file : null;
    }
    return null;
  };
  StPreview.prototype.prefetchFor = function (t) {
    if (this.destroyed || !t || !t.closest || saveData()) return;
    var label = t.closest('label'), input = null;
    if (label) input = label.control || (label.htmlFor ? document.getElementById(label.htmlFor) : label.querySelector('input'));
    else input = t.closest('input');
    if (!input || (input.type !== 'radio' && input.type !== 'checkbox')) return;
    var file = this.fileForControl(input);
    if (file && !this.cache.get(file)) this.image(file);
  };
  // warm the browser's HTTP cache with one layer (same srcset/sizes as a real load so the same width is chosen);
  // the Image is released after it loads, so this holds no decoded bitmaps. Returns a promise that always resolves.
  StPreview.prototype.warm = function (file) {
    var self = this;
    if (this.destroyed || this.cache.get(file) || (this.warmed && this.warmed[file])) return Promise.resolve();
    this.warmed = this.warmed || {}; this.warmed[file] = true;
    return new Promise(function (resolve) {
      var img = new Image();
      var done = function () { img.onload = img.onerror = null; img = null; resolve(); };
      img.onload = done; img.onerror = function () { delete self.warmed[file]; done(); };
      img.sizes = self.manifest.sizes;
      img.srcset = srcset(self.manifest, file);
      img.src = url(self.manifest, file, fallbackWidth(self.manifest));
    });
  };
  // idle scheduling that can be cancelled by destroy()
  StPreview.prototype.idle = function (fn) {
    var self = this, h = {};
    var run = function () { self.idles = self.idles.filter(function (x) { return x !== h; }); if (!self.destroyed) fn(); };
    if ('requestIdleCallback' in window) { h.idle = requestIdleCallback(run, { timeout: 4000 }); } else { h.timer = setTimeout(run, 2000); }
    this.idles.push(h);
  };
  // After the page settles: the other hardware finish is loaded and kept (one layer, instant toggle), never on
  // data saver, not while the tab is hidden (deferred to the next visibilitychange), never after teardown. On a
  // fast connection and a wide screen the colour layers are then warmed into the HTTP cache one at a time, in tile
  // order, each on its own idle slot, so a first click on any colour is a cache hit plus a decode. Phones and slow
  // or metered connections keep loading colours on demand. Warming is best effort: it is not a promise that every
  // colour is instant after any particular time.
  StPreview.prototype.prefetchAtIdle = function () {
    var self = this;
    this.idle(function () {
      try {
        if (self.destroyed || saveData()) return;
        if (document.hidden) { self.on(document, 'visibilitychange', function onVis() { if (!document.hidden) { document.removeEventListener('visibilitychange', onVis); self.prefetchAtIdle(); } }); return; }
        var sel = self.readSelection(), roles = self.view.roles, queue = [];
        for (var role in roles) {
          if (!own.call(roles, role)) continue;
          var def = roles[role];
          for (var v in def.values) {
            if (!own.call(def.values, v)) continue;
            var f = def.values[v];
            if (!f || v === sel[role] || f === self.current[role]) continue;
            if (def.source.type === 'variant') self.image(f); else queue.push(f);
          }
        }
        if (!queue.length || !fastConnection() || !window.matchMedia || !matchMedia('(min-width: 1024px)').matches) return;
        var next = function () {
          if (self.destroyed || !queue.length) return;
          if (document.hidden) { self.idle(next); return; }
          self.warm(queue.shift()).then(function () { if (!self.destroyed) self.idle(next); });
        };
        self.idle(next);
      } catch (e) {}
    });
  };

  // ---------- signals (all removable; see destroy) ----------
  StPreview.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.offs.push(function () { target.removeEventListener(type, fn, opts); });
  };
  StPreview.prototype.galleryColourControl = function (target) {
    var input = target && target.closest && target.closest('input, select');
    if (!input && target && target.closest) {
      var label = target.closest('label');
      if (label) input = label.control || (label.htmlFor ? document.getElementById(label.htmlFor) : label.querySelector('input'));
    }
    if (!input || !this.section.contains(input) || !input.closest('.gpo-app') || input.disabled || input.closest('fieldset[disabled]')) return null;
    if (input.tagName !== 'SELECT' && (input.tagName !== 'INPUT' || input.type !== 'radio')) return null;
    return /^(Colour|Handle Colour|Accent Colour)$/.test(input.getAttribute('data-field-name')) ? input : null;
  };
  StPreview.prototype.colourGalleryIntent = function (event) {
    if (!event.isTrusted || this.destroyed || !this.manifest.product || this.section.getAttribute('data-section-type') !== 'product') return false;
    var gallery = this.root.closest('product-images');
    if (gallery && gallery.querySelector('st-preview') !== this.root) return false;
    var ids = { 'biothane-traffic-handle-leash': '7595214536874', 'handsfree-leash-adapter': '7063451402410', 'adjustable-biothane-quick-release-collar-updated': '7892919877802', 'safety-strap-draft-build': '15426793898154', 'multi-dog-attachment-draft-build': '15426793996458', 'safety-strap': '7882843947178', 'multi-dog-attachment': '7881533489322', 'classic-biothane-dog-collar-all-colours': '15426609905834', 'classic-biothane-dog-leash-all-colours': '15426609217706', 'classic-biothane-leash-terracotta': '6904941707434' };
    if (ids[this.manifest.product.handle] !== String(this.manifest.product.id)) return false;
    var input = this.galleryColourControl(event.target);
    if (!input) return false;
    var field = input.getAttribute('data-field-name'), value = input.value;
    if (input.tagName === 'SELECT') {
      if (event.type !== 'input' && event.type !== 'change') return false;
      var enabled = Array.prototype.some.call(input.options, function (option) {
        return option.value === value && !option.disabled && !(option.parentNode.tagName === 'OPTGROUP' && option.parentNode.disabled);
      });
      if (!enabled) return false;
    } else {
      if (input.tagName !== 'INPUT' || input.type !== 'radio') return false;
      if (event.type === 'keyup' && /^(ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Space)$/.test(event.code)) value = this.field(field);
      else if (event.type !== 'click') return false;
    }
    if (!value || !/^(Colour|Handle Colour|Accent Colour)$/.test(field)) return false;
    for (var role in this.view.roles) {
      if (!own.call(this.view.roles, role)) continue;
      var source = this.view.roles[role].source;
      if (source.type === 'globo' && source.field === field) {
        // Both preview angles update together: preserve the shopper's current preview angle.
        // A colour chosen while browsing an original photo still returns to the front preview.
        var activeId = gallery && gallery.getAttribute('data-active-media');
        var activeSlide = gallery && ((activeId && gallery.querySelector('[data-slide][data-media-id="' + cssEscape(activeId) + '"]')) || gallery.querySelector('[data-slide].media--active'));
        var keepAngle = ['adjustable-biothane-quick-release-collar-updated', 'classic-biothane-leash-terracotta', 'classic-biothane-dog-leash-all-colours'].indexOf(this.manifest.product.handle) !== -1 && this.views.angle2 && activeSlide && activeSlide.querySelector('st-preview');
        this.galleryReturn = keepAngle ? null : { role: role, value: value, radio: input.tagName === 'INPUT' };
        this.schedule('colour-gallery');
        return true;
      }
    }
    return false;
  };
  StPreview.prototype.returnToPreview = function (req) {
    var intent = this.galleryReturn;
    if (!intent || this.destroyed || this.state !== 'ready') return;
    // Globo can deselect a radio on a repeat click. Show its verified example without selecting a form value.
    if (req.selection[intent.role] !== intent.value && !(intent.radio && !req.selection[intent.role] && req.example[intent.role])) return;
    this.galleryReturn = null;
    var slide = this.root.closest('[data-slide]'), gallery = this.root.closest('product-images');
    var id = slide && slide.getAttribute('data-media-id');
    if (!id || !gallery || !gallery.contains(slide) || slide.matches('.media--active') || gallery.getAttribute('data-active-media') === id) return;
    var tabindex = this.stage.getAttribute('tabindex');
    if (tabindex !== null) this.stage.removeAttribute('tabindex');
    try {
      this.root.dispatchEvent(new CustomEvent('theme:media:select', { bubbles: true, detail: { id: id } }));
    } finally {
      if (tabindex !== null) this.stage.setAttribute('tabindex', tabindex);
    }
  };
  StPreview.prototype.bind = function () {
    var self = this, re = function (why) { return function () { self.schedule(why); }; };
    ['gpoRenderCompleted', 'gpoElementChanged'].forEach(function (t) { self.on(document, t, re(t)); });
    var onInteract = function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var colourIntent = self.colourGalleryIntent(e);
      if (!colourIntent && e.isTrusted && self.section.contains(t) && e.type === 'click' && !self.galleryColourControl(t) && (t.closest('.gpo-app') || t.closest('input, select, button'))) self.galleryReturn = null;
      if (t.closest('.gpo-app') || t.matches('input[data-single-option-selector], select[data-single-option-selector]')) self.schedule(e.type);
    };
    ['change', 'input', 'click'].forEach(function (t) { self.on(document, t, onInteract, { passive: true }); });
    this.on(this.section, 'keyup', function (e) { self.colourGalleryIntent(e); });
    var gallery = this.root.closest('product-images');
    if (gallery) this.on(gallery, 'theme:media:select', function () { self.galleryReturn = null; });
    ['pointerdown', 'keydown'].forEach(function (type) {
      self.on(self.section, type, function (e) {
        if (!e.isTrusted || !e.target || !e.target.closest) return;
        if (e.target.closest('product-images, product-thumbs') || (e.target.closest('input, select, button, label') && !self.galleryColourControl(e.target))) self.galleryReturn = null;
      }, { passive: true });
    });
    this.on(this.section, 'theme:variant:change', re('theme:variant:change'));
    // prefetch on intent: a pointer over (or keyboard focus on) a colour tile / hardware pill starts that layer loading,
    // so the click that usually follows ~150-300 ms later finds it decoded. Never on data saver.
    var onIntent = function (e) { try { self.prefetchFor(e.target); } catch (err) {} };
    this.on(document, 'pointerover', onIntent, { passive: true });
    this.on(document, 'focusin', onIntent, { passive: true });
    this.on(window, 'pageshow', function (e) { if (e.persisted) self.schedule('pageshow'); });
    // defensive: Globo (re)inserted without the documented event; watch the option column only
    var scope = this.section.querySelector('.product__content') || this.section;
    this.mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) for (var j = 0; j < muts[i].addedNodes.length; j++) {
        var n = muts[i].addedNodes[j];
        if (n.nodeType === 1 && (n.matches('gpo-container, .gpo-app') || n.querySelector('.gpo-app'))) { self.schedule('mutation'); return; }
      }
    });
    this.mo.observe(scope, { childList: true, subtree: true });
  };
  StPreview.prototype.destroy = function () {
    this.destroyed = true;
    this.galleryReturn = null;
    if (this.cartRestoreStop) this.cartRestoreStop();
    this.offs.forEach(function (off) { off(); }); this.offs = [];
    if (this.mo) this.mo.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
    clearTimeout(this.settle);
    if (this.inflight) this.inflight();
    this.idles.forEach(function (h) { if (h.timer) clearTimeout(h.timer); else if (h.idle !== undefined && 'cancelIdleCallback' in window) cancelIdleCallback(h.idle); });
    this.idles = [];
    this.latest = -1; // any in-flight load sees req.seq !== latest and drops its result
  };

  // ---------- boot ----------
  function boot(scope) {
    (scope || document).querySelectorAll('st-preview:not([data-booted])').forEach(function (el) {
      el.setAttribute('data-booted', '');
      el.stPreview = new StPreview(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); }); else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    e.target.querySelectorAll('st-preview[data-booted]').forEach(function (el) { if (el.stPreview) el.stPreview.destroy(); });
  });
})();

